import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { ReimburseService } from '../../../../core/api/reimburse.service';
import { TagService } from '../../../../core/api/tag.service';
import { ExpanseCategoryService } from '../../../../core/api/expanse-category.service';
import { FileService } from '../../../../core/api/file.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { HelpersService } from '../../../../core/services/helpers.service';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { LoadingService } from '../../../../core/services/loading.service';
import { DirectiveModule } from '../../../../core/directives';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FilePreviewComponent } from '../../../../components/file-preview/file-preview.component';
import { AuthService } from '../../../../core/services/auth.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-action-reimbursement-claimer',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatDividerModule,
    FormsModule,
    ReactiveFormsModule,
    DirectiveModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './action-reimbursement-claimer.component.html',
  styleUrl: './action-reimbursement-claimer.component.scss',
})
export class ActionReimbursementClaimerComponent {
  m: any;
  id: any;
  form: FormGroup;
  tag: any[] = [];
  expenseCategory: any[] = [];
  uploadStatus: boolean = false;
  fileExists: boolean = false;
  fileUrl: any;
  file: any;
  fileName: string = '';
  uploadedFiles: any[] = [];
  canApprove: boolean = false;
  maxAmount: number = 0;
  projectTagNumber: string = '';
  projectTagName: string = '';
  tagSearchTerm: string = '';
  currentUser: any;
  canCreateProjectTag: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private helpers: HelpersService,
    private fb: FormBuilder,
    private router: Router,
    private reimburseService: ReimburseService,
    private dialog: MatDialog,
    private tagService: TagService,
    private expanseCategoryService: ExpanseCategoryService,
    private fileService: FileService,
    private loadingService: LoadingService,
    private authService: AuthService
  ) {
    this.m = this.activatedRoute.snapshot.queryParams['m'];
    this.id = this.activatedRoute.snapshot.queryParams['id'];
    this.form = this.fb.group({
      title: [null, [Validators.required]],
      amount: [null, Validators.required],
      currency: ['Rp', [Validators.required]],
      expanseCategoryId: [null, [Validators.required]],
      date: [null, [Validators.required]],
      tagId: [null, [Validators.required]],
      description: [null, [Validators.required]],
      location: [null, [Validators.required]],
      fileId: [null, [Validators.required]],
    });
    this.currentUser = this.authService.getLoggedUser();
    this.canCreateProjectTag =
      this.authService.isFinance(this.currentUser) ||
      this.authService.isApprover(this.currentUser);

    // Jalankan semua proses asynchronous secara paralel
    this.loadingService.startLoading();
    const tagPromise = this.getTag();
    const expanseCategoryPromise = this.getExpanseCategory();
    const dataByIdPromise = this.getDataById();

    // Tunggu semua proses selesai
    Promise.all([tagPromise, expanseCategoryPromise, dataByIdPromise]).finally(
      () => {
        this.loadingService.endLoading();
      }
    );
  }

  getTag(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.tagService.getAllData().then(
        (res) => {
          this.tag = res;
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  get filteredTag(): any[] {
    const keyword = this.tagSearchTerm.trim().toLowerCase();
    if (!keyword) return this.tag;
    return this.tag.filter((item: any) =>
      (item?.name ?? '').toLowerCase().includes(keyword)
    );
  }

  onTagSelectOpenedChange(opened: boolean): void {
    if (!opened) this.tagSearchTerm = '';
  }

  getExpanseCategory(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.expanseCategoryService.getAllData().then(
        (res) => {
          this.expenseCategory = res.filter(
            (item: any) => item.type == 'Reimbursment'
          );
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getDataById(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.id) {
        this.reimburseService.getById(this.id).then(
          (data) => {
            if (data.file) {
              this.file = data.file;
              this.fileExists = true;
              data['fileId'] = data.file.id;
              this.fileName = data.file.fileName;
              this.uploadedFiles = [data.file];
            }
            if (data.fileIds?.length && !this.uploadedFiles.length) {
              this.uploadedFiles = data.fileIds.map((id: number) => ({
                id,
                fileName: `File #${id}`,
              }));
            }
            data['expanseCategoryId'] = data.expanseCategory.id;
            data['tagId'] = data.tag.id;
            this.form.patchValue(data);
            resolve();
          },
          (error) => {
            this.helpers.sbError(error.message);
            reject(error);
          }
        );
      } else {
        resolve();
      }
    });
  }

  cancel() {
    this.router.navigate(['/dashboard/reimbursement-claimer']);
  }

  submit(status: string) {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const data = this.form.value;
      console.log(data);
      data['status'] = status;
      data['amount'] = this.helpers.parseAmount(data.amount);
      if (this.uploadedFiles.length) {
        data['fileIds'] = this.uploadedFiles.map((item: any) => item.id);
        data['fileId'] = this.uploadedFiles[0].id;
      }
      this.dialog
        .open(ConfirmDialogComponent, {
          width: '545px',
          maxHeight: '95vh',
          panelClass: 'confirm-dialog',
          data: {
            title: `Confirm ${
              this.m == 'add' ? 'Add New Reimbursement' : 'Edit Reimbursement'
            }`,
            message: `Changes to the Reimbursement cannot be undone once saved. Are you sure you want to proceed?`,
          },
        })
        .afterClosed()
        .subscribe((result: any) => {
          if (result) {
            if (this.m == 'add') {
              this.loadingService.startLoading();
              this.reimburseService
                .insert(data)
                .then(() => {
                  this.loadingService.endLoading();
                  this.helpers.sbSuccess('Reimbursement created!');
                  this.router.navigate(['/dashboard/reimbursement-claimer']);
                })
                .catch((error) => {
                  this.loadingService.endLoading();
                  this.helpers.sbError(error.message);
                });
            } else if (this.m == 'edit' && this.id) {
              this.loadingService.startLoading();
              this.reimburseService
                .updateById(this.id, data)
                .then(() => {
                  this.loadingService.endLoading();
                  this.helpers.sbSuccess('Reimbursement updated!');
                  this.router.navigate(['/dashboard/reimbursement-claimer']);
                })
                .catch((error) => {
                  this.loadingService.endLoading();
                  this.helpers.sbError(error.message);
                });
            }
          }
        });
    } else if (this.form.get('fileId')?.invalid) {
      this.helpers.sbError(
        'Please upload file firstly and fill all required fields!'
      );
    } else {
      this.helpers.sbError('Lengkapi form terlebih dahulu!');
    }
  }

  uploadFile(event: Event) {
    this.uploadStatus = true;
    const target = event.target as HTMLInputElement | null;
    const fileList = target?.files;
    if (fileList?.length) {
      const files = Array.from(fileList);
      const uploads = files.map(
        (file) =>
          new Promise<any>((resolve, reject) => {
            try {
              this.fileService.upload(file).subscribe({
                next: (res: any) => {
                  if (
                    res?.type === HttpEventType.Response &&
                    res?.body?.data
                  ) {
                    resolve(res.body.data);
                  }
                },
                error: (err: any) => reject(err),
              });
            } catch (err: any) {
              reject(err);
            }
          })
      );

      this.loadingService.startLoading();
      Promise.all(uploads)
        .then((results: any[]) => {
          const uploaded = results.filter(Boolean);
          if (!uploaded.length) return;

          this.uploadedFiles = [...this.uploadedFiles, ...uploaded];
          this.file = this.uploadedFiles[0];
          this.fileName =
            this.uploadedFiles.length === 1
              ? this.uploadedFiles[0].fileName
              : `${this.uploadedFiles.length} files selected`;
          this.form.get('fileId')?.setValue(this.uploadedFiles[0].id);
          this.fileExists = true;
          this.helpers.sbSuccess('File berhasil diupload');
        })
        .catch((err: any) => {
          this.helpers.sbError(err.message);
        })
        .finally(() => {
          this.loadingService.endLoading();
          this.uploadStatus = false;
          if (target) target.value = '';
        });
    } else {
      this.helpers.sbError('Tidak ada file yang akan diupload!');
      this.uploadStatus = false;
      if (target) target.value = '';
    }
  }

  download(fileItem?: any) {
    const targetFile = this.helpers.resolveTargetFile(fileItem, this.file);
    if (targetFile?.id) {
      this.dialog
        .open(FilePreviewComponent, {
          width: '70vw',
          maxHeight: '95vh',
          maxWidth: '70vw',
          data: targetFile,
        })
        .afterClosed()
        .subscribe((item) => {
          if (item) {
          }
        });
    } else {
      this.helpers.sbError('Tidak ada file yang bisa diunduh');
    }
  }

  removeUploadedFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    if (!this.uploadedFiles.length) {
      this.file = null;
      this.fileName = '';
      this.fileExists = false;
      this.form.get('fileId')?.setValue(null);
      return;
    }

    this.file = this.uploadedFiles[0];
    this.fileName =
      this.uploadedFiles.length === 1
        ? this.uploadedFiles[0].fileName
        : `${this.uploadedFiles.length} files selected`;
    this.form.get('fileId')?.setValue(this.uploadedFiles[0].id);
  }

  setMaxAmount(data: any): void {
    const found = this.expenseCategory.find((item) => item.id === data.value);
    if (found) {
      this.maxAmount = found.max_transaction_value;

      const amountControl = this.form.get('amount');
      if (this.maxAmount != 0 && amountControl) {
        amountControl.setValidators([
          Validators.required,
          Validators.max(this.maxAmount),
        ]);
        amountControl.updateValueAndValidity();
      } else if (this.maxAmount == 0 && amountControl) {
        amountControl?.setValidators([Validators.required]);
        amountControl?.updateValueAndValidity();
      }
    }
  }

  createProjectTag(): void {
    const projectNumber = this.projectTagNumber.trim();
    const projectName = this.projectTagName.trim();
    if (!projectNumber || !projectName) {
      this.helpers.sbError('Nomor Project dan Nama Project wajib diisi');
      return;
    }
    const name = `${projectNumber} - ${projectName}`;

    const payload = {
      name,
      budget: 0,
      description: `Tag Nomor Project - ${name}`,
      teams: [],
      expanse_categories: [],
    };

    this.loadingService.startLoading();
    this.tagService
      .insert(payload)
      .then((createdTag: any) => {
        this.tag.push(createdTag);
        this.form.get('tagId')?.setValue(createdTag.id);
        this.projectTagNumber = '';
        this.projectTagName = '';
        this.helpers.sbSuccess('Tag Nomor Project berhasil ditambahkan');
      })
      .catch((error) => {
        this.helpers.sbError(error.message);
      })
      .finally(() => {
        this.loadingService.endLoading();
      });
  }
}
